import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleButton } from "@/pages/LoginPage";
import { AuthCard } from "@/pages/SignupPage";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, ROLE_LABELS } from "@/lib/api";

export default function AcceptInvitePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [token] = useState(() => params.get("token") || "");
  const [invite, setInvite] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [previewError, setPreviewError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) {
      setPreviewError("This invitation link is incomplete.");
      return;
    }
    window.history.replaceState(null, "", window.location.pathname);
    api
      .post("/auth/invitations/preview", { token })
      .then(({ data }) => setInvite(data))
      .catch((requestError) => setPreviewError(errMsg(requestError)));
  }, [token]);

  if (user) return <Navigate to="/" replace />;
  const submit = async (event) => {
    event.preventDefault();
    if (password !== confirm) {
      setSubmitError("The passwords don't match");
      return;
    }
    setBusy(true);
    setSubmitError("");
    try {
      const { data } = await api.post("/auth/invitations/accept", {
        token,
        password,
      });
      setUser(data);
      toast.success("Your account is ready");
      navigate(data.role === "technician" ? "/prep" : "/chat", {
        replace: true,
      });
    } catch (requestError) {
      setSubmitError(errMsg(requestError));
    } finally {
      setBusy(false);
    }
  };
  const useGoogle = () => {
    sessionStorage.setItem("aj-invite-token", token);
    return true;
  };

  return (
    <AuthCard
      title="Set up your account"
      subtitle={
        invite
          ? `${invite.school_name} · ${ROLE_LABELS[invite.role] || invite.role}`
          : previewError
            ? "Invitation unavailable"
            : "Checking invitation"
      }
    >
      {previewError ? (
        <p className="text-sm text-destructive" data-testid="invite-error">
          {previewError}
        </p>
      ) : !invite ? (
        <p
          className="text-sm text-muted-foreground"
          data-testid="invite-loading"
        >
          Checking your invitation…
        </p>
      ) : (
        <>
          <p className="mb-5 text-sm" data-testid="invite-summary">
            You’re joining as <b>{invite.name}</b> using <b>{invite.email}</b>.
          </p>
          <form
            onSubmit={submit}
            className="space-y-4"
            data-testid="invite-password-form"
          >
            {submitError && (
              <p
                className="text-sm text-destructive"
                data-testid="invite-submit-error"
              >
                {submitError}
              </p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="invite-password">Choose a password</Label>
              <Input
                id="invite-password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                data-testid="invite-password-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="invite-password-confirm">Confirm password</Label>
              <Input
                id="invite-password-confirm"
                type="password"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
                data-testid="invite-password-confirm-input"
              />
            </div>
            <Button
              type="submit"
              className="h-11 w-full"
              disabled={busy}
              data-testid="invite-password-submit-button"
            >
              {busy ? "Creating account…" : "Create account"}
            </Button>
          </form>
          <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
            <div className="h-px flex-1 bg-border" />
            or
            <div className="h-px flex-1 bg-border" />
          </div>
          <GoogleButton
            before={useGoogle}
            label="Set up with Google"
            testId="invite-google-button"
          />
        </>
      )}
    </AuthCard>
  );
}
